import Amenity from '@customTypes/amenity';

type StringOrArrayOfStrings = string | string[];
const flatten = (arr: StringOrArrayOfStrings[]): [string, number, number][] =>
  arr.flatMap((entry, index) => {
    if (Array.isArray(entry)) {
      return entry.map((e, subIndex) => [e, index, subIndex] as [string, number, number]);
    }
    return [[entry, index, 0]] as [string, number, number][];
  });

const weightedAmenities: Record<string, StringOrArrayOfStrings[]> = {
  all: [
    ['rooftop-pool', 'infinity-pool', 'indoor-pool', 'outdoor-pool'],
    'all-inclusive',
    'airport-shuttle',
    'cruise-port-shuttle',
    'waterpark',
    'beach',
    'Adults Only',
    ['Family-Friendly', 'Party', 'Luxe'],
    [
      'spa',
      'infrared-sauna',
      'sauna',
      'salt-room',
      'steam-room',
      'cold-plunge',
      'thermal-pool',
      'led-and-red-light-therapy',
    ],
    'free-parking',
    'hottub',
    'lazyriver',
    'kiddie-pool',
    'splash-pad',
    'waterslide',
    'fitness-center',
    'luggage-storage',
    'wheelchair',
  ],
  pool: [
    ['rooftop-pool', 'infinity-pool', 'indoor-pool', 'outdoor-pool'],
    'lazyriver',
    'waterpark',
    'beach',
    ['Family-Friendly', 'Party', 'Luxe'],
    'Adults Only',
    'all-inclusive',
    'airport-shuttle',
    'cruise-port-shuttle',
    'free-parking',
    'kiddie-pool',
    'hottub',
    'splash-pad',
    'waterslide',
    'luggage-storage',
  ],
  spa: [
    'infrared-sauna',
    'sauna',
    'steam-room',
    'salt-room',
    'led-and-red-light-therapy',
    'cold-plunge',
    'thermal-pool',
    'free-parking',
    'Luxe',
    'airport-shuttle',
    'cruise-port-shuttle',
    'fitness-center',
  ],
  'day room': [
    ['rooftop-pool', 'infinity-pool', 'indoor-pool', 'outdoor-pool'],
    'beach',
    'waterpark',
    'hottub',
    'spa',
    'lazyriver',
    'all-inclusive',
    'airport-shuttle',
    'fitness-center',
    'cruise-port-shuttle',
    'Family-Friendly',
    'Adults Only',
    'free-parking',
    'waterslide',
    'splash-pad',
    'kiddie-pool',
    'wheelchair',
  ],
};

const priorityMaps: Record<string, Map<string, [number, number]>> = {
  all: new Map(
    flatten(weightedAmenities.all).map(([name, index, subIndex]) => [name, [index, subIndex]]),
  ),
  pool: new Map(
    flatten(weightedAmenities.pool).map(([name, index, subIndex]) => [name, [index, subIndex]]),
  ),
  spa: new Map(
    flatten(weightedAmenities.spa).map(([name, index, subIndex]) => [name, [index, subIndex]]),
  ),
  'day room': new Map(
    flatten(weightedAmenities['day room']).map(([name, index, subIndex]) => [
      name,
      [index, subIndex],
    ]),
  ),
};

/**
 * Sorts amenities based on an internal priority list.
 * Pass the optional count parameter to get top rated amenities.
 * The top rated amenities take into consideration the hierarchy defined in the priority list.
 * @param category string
 * @param amenities Amenity[]
 * @param count number
 * @returns Amenity[]
 */
export function getWeightedAmenities(category: string, amenities: Amenity[], count?: number) {
  const priorityMap = !category ? priorityMaps.all : priorityMaps[category];
  const sortedAmenities = [...amenities]
    .filter((amenity) => priorityMap.has(amenity.name))
    .sort((a, b) => {
      const aPriority = priorityMap.has(a.name) ? priorityMap.get(a.name)! : [Infinity, 0];
      const bPriority = priorityMap.has(b.name) ? priorityMap.get(b.name)! : [Infinity, 0];

      if (aPriority[0] !== bPriority[0]) {
        return aPriority[0] - bPriority[0];
      }
      return aPriority[1] - bPriority[1];
    });

  if (Number.isInteger(count)) {
    const sortedArr: Amenity[] = [];
    let j = 0;
    let i = 0;
    while (i < count! && j < sortedAmenities.length) {
      const prevPriorityLevel = sortedAmenities[j - 1]
        ? priorityMap.get(sortedAmenities[j - 1].name)?.[0]
        : undefined;
      const currentPriorityLevel = priorityMap.get(sortedAmenities[j].name)?.[0];
      if (prevPriorityLevel !== currentPriorityLevel) {
        sortedArr[i] = sortedAmenities[j];
        i += 1;
        j += 1;
      } else {
        j += 1;
      }
    }
    return sortedArr;
  }

  return sortedAmenities;
}
